import { rem } from 'polished'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { getMerchandiseCategorySearchUrl, getMerchandiseBrandSearchUrl, getMerchandiseSearchUrl } from '../merchandiseUtils'
import type { ImageCarouselRefInterface } from 'LuxKit/Carousel/BaseImageCarousel'
import type { ImageParams } from 'components/Common/ResponsiveImage'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { middleDotSeparatorMessage } from 'LuxKit/intl/separators'
import { take } from 'lib/array/arrayUtils'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import Clickable from 'components/Common/Clickable'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import HeadData from 'components/Common/HeadData'
import Image from 'components/Common/Image'
import ImageCarousel from 'LuxKit/Carousel/ImageCarousel'
import LayoutContainer from 'components/Common/LayoutContainer'
import MerchandiseFreeShippingLabel from 'components/Merchandise/MerchandiseFreeShippingLabel'
import MerchandiseSaleLabel from 'components/Merchandise/MerchandiseSaleLabel'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import MerchandiseOfferCarousel from '../MerchandiseCarousels/MerchandiseOfferCarousel'
import MerchandiseOfferPageBreadcrumbs from './MerchandiseOfferPageBreadcrumbs'
import MerchandiseOfferPageDetails from './MerchandiseOfferPageDetails'
import MerchandiseOfferPageOverviewSection from './MerchandiseOfferPageOverviewSection'
import MerchandiseRecentlyViewedCarousel from '../MerchandiseCarousels/MerchandiseRecentlyViewedCarousel'
import Sheet from 'components/utils/Sheet'
import updateRecentlyViewedMerchandise from 'actions/merchandise/updateRecentlyViewedMerchandise'
import useMerchandiseCategory from 'hooks/Merchandise/useMerchandiseCategory'
import useMerchandiseBrand from 'hooks/Merchandise/useMerchandiseBrand'
import config from 'constants/config'
import { getImageUrl } from 'lib/image/imageUtils'

const previewImageBaseWidth = 300
const previewImageLargeWidth = 500

const ContentGrid = styled(Sheet)`
  display: grid;
  grid-template-areas: "images"
  "details"
  "content";
  gap: ${rem(24)};

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(600)} 1fr;
    grid-template-areas: "images details"
    "content details";
    gap: ${rem(60)};
  }
  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: ${rem(685)} 1fr;
  }
`

const ImagesContainer = styled.div`
  display: grid;
  grid-area: images;
  margin-inline-start: ${rem(-16)};
  width: calc(100% + ${rem(32)});
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    margin-inline-start: 0;
    width: auto;
  }
`

const ImageCarouselContainer = styled(ImageCarousel)`
  height: ${rem(previewImageBaseWidth)};

  ${mediaQueryUp.tablet} {
    height: ${rem(previewImageLargeWidth)};
  }
`

const DetailsContainer = styled.div`
  grid-area: details;
`

const OverviewContainer = styled.div`
  grid-area: content;
`

const PreviewImageContainer = styled(CSSBreakpoint)`
  display: grid;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: ${rem(200)};
    gap: ${rem(16)};
  }
`

const PreviewImage = styled(Image)`
  width: 100%;
`

const imageParams: ImageParams = {
  mobileWidth: '685px',
  objectFit: 'contain',
}

interface Props {
  offer: App.MerchandiseOffer
}

function MerchandiseOfferPageContent(props: Props) {
  const { offer } = props
  const intl = useIntl()
  const defaultVariant = offer.variants.find(v => (v.inventory ?? 0) > 0) ?? offer.variants[0]
  const [activeVariant, setActiveVariant] = useState<App.MerchandiseOfferVariant>(defaultVariant)
  const [quantity, setQuantity] = useState(1)
  const isMobile = useScreenSizeOnly('mobile')

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(updateRecentlyViewedMerchandise(offer.id))
  }, [offer.id, dispatch])

  const [brand] = useMerchandiseBrand(offer.brand.id)
  const [brandFilters, brandSearchLink] = useMemo((): [App.MerchandiseFilters, string] => {
    return [
      {
        brands: [offer.brand.id],
        excludedAdvertIds: [offer.id],
      },
      brand ? getMerchandiseBrandSearchUrl(brand) : getMerchandiseSearchUrl({ brands: [offer.brand.id] }),
    ]
  }, [offer.brand.id, offer.id, brand])

  const [category] = useMerchandiseCategory({ id: offer.category?.id })
  const [categoryFilters, categoryQuery] = useMemo((): [App.MerchandiseFilters, string] | [undefined, ''] => {
    if (category) {
      return [{
        categories: [category.id],
        excludedAdvertIds: [offer.id],
      },
      getMerchandiseCategorySearchUrl(category),
      ]
    }
    return [undefined, '']
  }, [category, offer.id])

  const carouselRef = useRef<ImageCarouselRefInterface | null>(null)
  const onPreviewImageClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>((e) => {
    const imageIndex = e.currentTarget.dataset.index
    if (imageIndex) {
      const index = parseInt(imageIndex, 10)
      carouselRef.current?.goToImage(activeVariant.images[index])
    }
  }, [activeVariant.images])

  const onVariantChange = useCallback((variant: App.MerchandiseOfferVariant) => {
    setActiveVariant(variant)
    // Preserve quantity if new variant has sufficient inventory, otherwise cap to max available
    setQuantity(prev => Math.min(prev, variant.inventory ?? 1))
  }, [])

  const alsoFoundInTrackingTitle = intl.formatMessage(
    {
      id: 'J6M9CKAULCpyZxeTJdzQ',
      defaultMessage: 'Also found in {brandName}',
      description: 'Tracking title for carousel showing more products from the same brand',
    },
    { brandName: offer.brand.name },
  )

  const alsoFoundInDisplayTitle = <FormattedMessage
    id="eVPsRnyHMJvCxaQSvDhm"
    defaultMessage="Also found in <italic>{brandName}</italic>"
    description="Display title for carousel showing more products from the same brand"
    values={{
      brandName: offer.brand.name,
      italic: (chunks) => <i>{chunks}</i>,
    }}
  />

  const title = intl.formatMessage(
    {
      id: 'G3rlLFL37k7SJVj4F5lE',
      defaultMessage: 'Similar items to {offerName}',
      description: 'Title for similar items carousel when offer name is short',
    },
    { offerName: offer.name },
  )

  const similarItemsYouMightLike = <FormattedMessage
    id="JNEyNlDd1jrQRmhAeKkb"
    defaultMessage="Similar items <italic>you</italic> might like"
    description="Display title for similar items carousel when offer name is long"
    values={{
      italic: (chunks) => <i>{chunks}</i>,
    }}
  />

  const similarItemsToOffer = <FormattedMessage
    id="SKumLKjhdxhLpn6CN895"
    defaultMessage="Similar items to <italic>{offerName}</italic>"
    description="Display title for similar items carousel when offer name is short"
    values={{
      offerName: offer.name,
      italic: (chunks) => <i>{chunks}</i>,
    }}
  />

  const similarItemsDisplayTitle = title.length > 30 ? similarItemsYouMightLike : similarItemsToOffer

  const trackingPageName = 'Offer Page'

  const previewImageWidth = useMemo(() => {
    return isMobile ? previewImageBaseWidth : previewImageLargeWidth
  }, [isMobile])

  const meta = useMemo(() => {
    const variantWithImage = offer.variants.find(variant => variant.images.length > 0)
    const heroImage = variantWithImage?.images[0] ?? offer.images[0]
    const imageId = heroImage?.id
    const imageUrl = imageId ? getImageUrl(imageId, { width: 1200, format: 'jpeg' }) : undefined
    const features = offer.copy?.features ?? []
    const sep = intl.formatMessage(middleDotSeparatorMessage)
    const featuresSummary = features.length > 0 ? features.slice(0, 3).join(` ${sep} `) : undefined
    const fallbackDescription = intl.formatMessage(
      {
        id: 'U4yQJV2PgrcxgC3No--s',
        defaultMessage: 'Shop {offerName} at {siteTitle} Shop.',
        description: 'Fallback meta description for merchandise offer page',
      },
      { offerName: offer.name, siteTitle: config.title },
    )
    const initialDescription = featuresSummary ?? fallbackDescription
    const description = initialDescription.length > 200 ? `${initialDescription.slice(0, 197)}...` : initialDescription
    const titleText = offer.brand?.name ? `${offer.name} | ${offer.brand.name}` : offer.name

    return {
      title: titleText,
      description,
      imageId,
      imageUrl,
      imageAlt: offer.name,
    }
  }, [offer, intl])

  return <div>
    <HeadData
      title={meta.title}
      description={meta.description}
      imageId={meta.imageId}
      imageUrl={meta.imageUrl}
      imageAlt={meta.imageAlt}
    />
    <LayoutContainer>
      <MerchandiseOfferPageBreadcrumbs offer={offer} alwaysExpand />
      <ContentGrid padding="0 0 40 0" tabletPadding="0 0 60 0">
        <ImagesContainer>
          <ImageCarouselContainer
            images={activeVariant.images}
            imageParams={imageParams}
            carouselRef={carouselRef}
            topLeft={<LabelGroup>
              <MerchandiseSaleLabel variants={[activeVariant]} />
              <MerchandiseFreeShippingLabel offer={offer} />
            </LabelGroup>}
          />
          {activeVariant.images.length > 1 && <PreviewImageContainer min="tablet">
            {take(activeVariant.images, 4).map((image, index) => <Clickable
              key={image.id}
              onClick={onPreviewImageClick}
              data-index={index}
            >
              <PreviewImage
                image={image}
                width={previewImageWidth}
                fit="center"
              />
            </Clickable>)}
          </PreviewImageContainer>}
        </ImagesContainer>
        <DetailsContainer>
          <MerchandiseOfferPageDetails
            quantity={quantity}
            offer={offer}
            variant={activeVariant}
            onVariantChange={onVariantChange}
            onQuantityChange={setQuantity}
          />
        </DetailsContainer>
        <OverviewContainer>
          <MerchandiseOfferPageOverviewSection offer={offer} />
        </OverviewContainer>
      </ContentGrid>
    </LayoutContainer>
    <MerchandiseOfferCarousel
      title={alsoFoundInDisplayTitle}
      viewAllTo={brandSearchLink}
      filters={brandFilters}
      trackingListName={alsoFoundInTrackingTitle}
      trackingPageName={trackingPageName}
    />
    {categoryFilters && <MerchandiseOfferCarousel
      title={similarItemsDisplayTitle}
      viewAllTo={categoryQuery}
      filters={categoryFilters}
      trackingListName={title}
      trackingPageName={trackingPageName}
    />}
    <MerchandiseRecentlyViewedCarousel
      trackingPageName={trackingPageName}
      excludeIds={[offer.id]}
    />
  </div>
}

export default MerchandiseOfferPageContent
